import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Building2,
  RefreshCw,
  Search,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { providersAPI } from '../services/api';

const ProvidersPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState(null);

  const canWrite = hasPermission('merchants', 'write');

  const fetchProviders = async (page = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const params = { page, page_size: pagination.pageSize };
      if (searchQuery) params.search = searchQuery;
      
      const response = await providersAPI.list(params);
      setProviders(response.data.providers || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (err) {
      toast.error('Ошибка загрузки провайдеров');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProviders(1, search);
  };

  const openCreateDialog = () => {
    setEditingProvider(null);
    setFormData({ name: '', description: '', is_active: true });
    setShowDialog(true);
  };

  const openEditDialog = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      description: provider.description || '',
      is_active: provider.is_active,
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Введите название провайдера');
      return;
    }

    try {
      setSaving(true);
      if (editingProvider) {
        await providersAPI.update(editingProvider.id, formData);
        toast.success('Провайдер обновлен');
      } else {
        await providersAPI.create(formData);
        toast.success('Провайдер создан');
      }
      setShowDialog(false);
      fetchProviders(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (provider) => {
    setDeletingProvider(provider);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingProvider) return;
    
    try {
      await providersAPI.delete(deletingProvider.id);
      toast.success('Провайдер удален');
      setDeleteDialog(false);
      setDeletingProvider(null);
      fetchProviders(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  return (
    <div className="space-y-6" data-testid="providers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Провайдеры (Эквайеры)</h1>
          <p className="text-slate-400 mt-1">
            Всего: {pagination.total.toLocaleString('ru-RU')} провайдеров
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-provider-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('common.create')}
            </Button>
          )}
          <Button
            onClick={() => fetchProviders(pagination.page)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Поиск по названию провайдера..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-700/50 border-slate-600 text-white"
            data-testid="provider-search-input"
          />
        </div>
        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
          Найти
        </Button>
      </form>

      {/* Providers Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : providers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Building2 className="w-12 h-12 mb-4 opacity-50" />
              <p>Провайдеры не найдены</p>
              {canWrite && (
                <Button
                  onClick={openCreateDialog}
                  className="mt-4 bg-emerald-500 hover:bg-emerald-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Добавить первого провайдера
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Название</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Описание</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Создан</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((provider) => (
                    <tr
                      key={provider.id}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                      data-testid={`provider-row-${provider.id}`}
                    >
                      <td className="p-4">
                        <span className="text-white font-medium">{provider.name}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 text-sm">
                          {provider.description || '—'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        {provider.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                            <Check className="w-3 h-3" />
                            {t('common.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400">
                            <X className="w-3 h-3" />
                            {t('common.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-slate-400 text-sm">
                          {provider.created_at
                            ? new Date(provider.created_at).toLocaleDateString('ru-RU')
                            : '—'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        {canWrite && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(provider)}
                              className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              data-testid={`edit-provider-${provider.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(provider)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                              data-testid={`delete-provider-${provider.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchProviders(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchProviders(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              {editingProvider ? 'Редактировать провайдера' : 'Новый провайдер'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Название *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Например: Jusan Bank"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
                data-testid="provider-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Описание</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Описание провайдера..."
                className="bg-slate-700/50 border-slate-600 text-white min-h-[80px]"
                data-testid="provider-description-input"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <Label className="text-slate-300">{t('common.active')}</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="provider-active-switch"
              />
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={saving}
                data-testid="save-provider-btn"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                ) : null}
                {editingProvider ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Удалить провайдера?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 mt-2">
            Вы уверены, что хотите удалить провайдера <strong>{deletingProvider?.name}</strong>?
            Это действие нельзя отменить.
          </p>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog(false)}
              className="border-slate-600 text-slate-300"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-provider-btn"
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProvidersPage;
